import type { LucideIcon } from "lucide-react";
import {
  ArrowLeftRight,
  BarChart3,
  CalendarClock,
  Clock,
  Coffee,
  Gauge,
  Hourglass,
  LayoutDashboard,
  ListTree,
  PhoneMissed,
  PhoneOff,
  PhoneOutgoing,
  Repeat,
  Smile,
  Timer,
  UserRoundCheck,
  Users,
  Activity,
  Scale,
} from "lucide-react";

export const ANALYTICS_ROLES = ["ROLE_REPORT", "ROLE_ADMIN", "ROLE_SUPER"];

export type AnalyticsPageMeta = {
  id: string;
  /** Caminho relativo a /analytics ("" = visão geral). */
  path: string;
  title: string;
  icon: LucideIcon;
  description: string;
  /** Dica de frequência de acompanhamento. */
  frequency: string;
};

/** Ordem = ordem do menu "Relatórios gerenciais". */
export const ANALYTICS_PAGES: AnalyticsPageMeta[] = [
  { id: "overview", path: "", title: "Visão geral", icon: LayoutDashboard, description: "Painel gerencial do período: volume, nível de serviço, abandono, tempos e quem mais atendeu, comparados ao período anterior.", frequency: "Diária (início e fim do expediente)" },
  { id: "volume", path: "volume", title: "Volume e picos", icon: BarChart3, description: "Chamadas ofertadas, atendidas e abandonadas ao longo do tempo e o mapa de picos por dia da semana e hora — base para dimensionar escala.", frequency: "Semanal" },
  { id: "sla", path: "sla", title: "Nível de serviço", icon: Gauge, description: "Percentual de chamadas atendidas dentro do tempo alvo (SLA) sobre as ofertadas que chegaram à fila.", frequency: "Diária" },
  { id: "times", path: "times", title: "TMA e TME", icon: Clock, description: "Tempo médio de atendimento (conversa) e de espera na fila, com o percentil 90 da espera.", frequency: "Diária" },
  { id: "abandonment", path: "abandonment", title: "Abandono", icon: PhoneOff, description: "Clientes que desligaram antes de serem atendidos: taxa, filas mais afetadas e quanto esperaram antes de desistir.", frequency: "Diária" },
  { id: "transfers", path: "transfers", title: "Transferências", icon: ArrowLeftRight, description: "Chamadas transferidas entre agentes/filas — indício de roteamento ou capacitação a ajustar.", frequency: "Semanal" },
  { id: "nps", path: "nps", title: "NPS", icon: Smile, description: "Net Promoter Score da pesquisa pós-atendimento: promotores menos detratores, por fila e por agente.", frequency: "Semanal / mensal" },
  { id: "capacity", path: "capacity", title: "Capacidade × demanda", icon: Scale, description: "Demanda (chamadas) comparada à capacidade (agentes logados e disponíveis) em cada intervalo.", frequency: "Semanal (planejamento de escala)" },
  { id: "ura", path: "ura", title: "URA", icon: ListTree, description: "Chamadas resolvidas ou encerradas na URA, sem chegar a uma fila ou agente.", frequency: "Mensal" },
  { id: "callbacks", path: "callbacks", title: "Rechamadas", icon: Repeat, description: "Clientes que ligaram mais de uma vez no período — sinal de problema não resolvido no primeiro contato.", frequency: "Semanal" },
  { id: "agents", path: "agents", title: "Agentes", icon: Users, description: "Distribuição das chamadas entre os agentes e o desempenho individual. Clique num agente para ver a linha do tempo do dia.", frequency: "Diária" },
  { id: "sessions", path: "sessions", title: "Jornada do agente", icon: UserRoundCheck, description: "Primeiro login, último logoff, tempo logado e aderência à escala prevista (atrasos e saídas antecipadas).", frequency: "Diária" },
  { id: "pauses", path: "pauses", title: "Pausas", icon: Coffee, description: "Quantidade e tempo de pausas por agente e por tipo.", frequency: "Diária" },
  { id: "occupancy", path: "occupancy", title: "Ocupação", icon: Activity, description: "Parcela do tempo logado em atendimento: (conversa + pós-atendimento) ÷ tempo logado.", frequency: "Semanal" },
  { id: "missed", path: "missed", title: "Não atendidas após distribuição", icon: PhoneMissed, description: "Chamadas que tocaram para o agente e não foram atendidas (RINGNOANSWER), por agente e ao longo do tempo.", frequency: "Diária" },
  { id: "intervals", path: "intervals", title: "Intervalo entre chamadas", icon: Hourglass, description: "Tempo médio e mediano entre o fim de uma chamada e o início da seguinte, por agente.", frequency: "Semanal" },
  { id: "outbound", path: "outbound", title: "Discador", icon: PhoneOutgoing, description: "Resultados por campanha do discador: tentativas, contato, conexão com agente/agente virtual e TMA.", frequency: "Diária durante campanhas" },
  { id: "schedules", path: "schedules", title: "Escala dos agentes", icon: CalendarClock, description: "Horário previsto de cada agente por dia da semana — usado para calcular aderência, atrasos e saídas antecipadas.", frequency: "Quando a escala mudar" },
];

export const TIMELINE_META = { title: "Linha do tempo do agente", icon: Timer, frequency: "Sob demanda (investigação)" };

export function analyticsHref(path: string) {
  return path ? `/analytics/${path}` : "/analytics";
}

export function pageMeta(id: string): AnalyticsPageMeta {
  const m = ANALYTICS_PAGES.find((p) => p.id === id);
  if (!m) throw new Error(`analytics page ${id}`);
  return m;
}

/** Referência visual de nível de serviço (80/20 de mercado) — linha tracejada nos gráficos de SLA. */
export const SLA_REFERENCE = 0.8;
